import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { WHATSAPP_URL, POLICIES } from "@/data/constants";

const GOLD = "#B89047";
const CHARCOAL = "#1A1A1A";
const OFF_WHITE = "#FDFCF8";
const BORDER = "#E5E0D8";

const STEPS = [
  {
    num: "01",
    title: "Contact via WhatsApp or Click Book & Pay",
    desc: "Send a WhatsApp message with your preferred service, date, city/country, and a brief description of your concern — or click Book & Pay on any service to open payment details.",
  },
  {
    num: "02",
    title: "Manual Confirmation",
    desc: "Akkshay Narula reviews your inquiry and manually confirms availability. You will receive a confirmation message with the exact appointment details.",
  },
  {
    num: "03",
    title: "Advance Payment via UPI or Bank Transfer",
    desc: "Pay 100% of the consultation fee in advance via UPI (7002862280) or Bank Transfer (A/c: 278201503229, IFSC: ICIC0003863). Send your payment screenshot on WhatsApp to confirm.",
  },
  {
    num: "04",
    title: "Your Consultation",
    desc: "Attend your session via the confirmed mode (call, chat, video, or in-person). International clients connect at the fixed IST timing. All sessions conducted personally by Akkshay Narula.",
  },
];

export default function ProcessPage() {
  useSEO({
    title: "Consultation Process & Booking Policies | Akkshay Narula",
    description: "How to book a consultation with Akkshay Narula — WhatsApp-based booking, advance payment policy, outstation visit rules, and what to expect from your session.",
    keywords: "how to book astrology consultation India, astrology consultation process, vastu consultation booking India",
    canonical: "/process",
  });
  return (
    <main data-testid="process-page" style={{ backgroundColor: OFF_WHITE }}>
      {/* Header */}
      <section style={{ padding: "72px 0 56px", borderBottom: `1px solid ${BORDER}`, backgroundColor: "#F5F3EB" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>
            How It Works
          </p>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.4rem, 5vw, 3.8rem)", fontWeight: 500, color: CHARCOAL, lineHeight: 1.1, maxWidth: "600px" }}>
            Consultation Process & Policies
          </h1>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#4A4A4A", marginTop: "16px", lineHeight: 1.7, maxWidth: "540px" }}>
            A structured, premium process designed for serious clients. Please read all policies before booking.
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section style={{ padding: "80px 0", borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "48px" }}>
            Step-by-Step Process
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {STEPS.map((step, i) => (
              <div
                key={step.num}
                data-testid={`process-step-${i + 1}`}
                style={{
                  padding: "40px",
                  borderRight: i % 2 === 0 ? `1px solid ${BORDER}` : "none",
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "3rem", fontWeight: 400, color: BORDER, lineHeight: 1, marginBottom: "16px" }}>
                  {step.num}
                </p>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", fontWeight: 500, color: CHARCOAL, marginBottom: "12px" }}>
                  {step.title}
                </h3>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#4A4A4A", lineHeight: 1.75 }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Policies */}
      <section style={{ padding: "80px 0", backgroundColor: "#F5F3EB", borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>
            Terms & Conditions
          </p>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "40px" }}>
            Booking Policies
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
            {POLICIES.map((policy, i) => (
              <div
                key={i}
                data-testid={`policy-item-${i + 1}`}
                style={{ display: "flex", gap: "16px", padding: "20px 0", borderBottom: `1px solid ${BORDER}`, alignItems: "flex-start" }}
              >
                <CheckCircle size={16} style={{ color: GOLD, flexShrink: 0, marginTop: "3px" }} />
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.92rem", color: "#4A4A4A", lineHeight: 1.7 }}>
                  {policy}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outstation */}
      <section style={{ padding: "64px 0", borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          <div style={{ border: `1px solid ${BORDER}`, padding: "40px" }}>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.18em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>
              Outstation Visit Policy
            </p>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.6rem", fontWeight: 500, color: CHARCOAL, marginBottom: "16px" }}>
              Travel & Accommodation
            </h3>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.92rem", color: "#4A4A4A", lineHeight: 1.75, marginBottom: "16px" }}>
              For locations outside Guwahati & Bengaluru, professional visit fees start from ₹1,00,000 per day within India and ₹5,00,000 per day internationally. All travel expenses are additional unless agreed otherwise.
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
              {["Round-trip flights", "Hotel accommodation", "Local transport", "Visas & documentation (international)", "Other necessary travel expenses"].map((item) => (
                <li key={item} style={{ display: "flex", gap: "10px", alignItems: "center", fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#4A4A4A" }}>
                  <span style={{ width: "6px", height: "6px", backgroundColor: GOLD, borderRadius: "50%", flexShrink: 0 }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* International Clients */}
      <section style={{ padding: "64px 0", backgroundColor: "#F5F3EB" }}>
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          <div style={{ border: `1px solid ${BORDER}`, padding: "40px" }}>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.18em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "12px" }}>
              International Clients
            </p>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.6rem", fontWeight: 500, color: CHARCOAL, marginBottom: "16px" }}>
              Global Consultation Guidelines
            </h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
              {[
                "All fees are charged in INR (Indian Rupees) regardless of your location.",
                "Session timings are fixed in IST (Indian Standard Time) — no exceptions.",
                "No last-minute schedule changes accepted for international consultations.",
                "WhatsApp is the primary communication channel for all international clients.",
              ].map((item, i) => (
                <li key={i} style={{ display: "flex", gap: "14px", alignItems: "flex-start", fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#4A4A4A", lineHeight: 1.65 }}>
                  <CheckCircle size={15} style={{ color: GOLD, flexShrink: 0, marginTop: "3px" }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: CHARCOAL, padding: "64px 0", textAlign: "center" }}>
        <div className="max-w-xl mx-auto px-6">
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 4vw, 2.6rem)", fontWeight: 500, color: "#FDFCF8", marginBottom: "20px" }}>
            Ready to Book Your Consultation?
          </h2>
          <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap" }}>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="process-whatsapp-cta"
              style={{ backgroundColor: GOLD, color: CHARCOAL, padding: "14px 28px", fontFamily: "'Outfit', sans-serif", fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", textDecoration: "none", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "8px" }}
            >
              Request via WhatsApp <ArrowRight size={14} />
            </a>
            <Link
              to="/consultations"
              data-testid="process-view-fees-btn"
              style={{ border: "1px solid #2A2A2A", color: "#8A8A8A", padding: "14px 28px", fontFamily: "'Outfit', sans-serif", fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "8px" }}
            >
              View All Fees <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
